
import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation} from '@angular/core';
import { TermChangeElement, TermChangeService } from '../../shared/services/termChange.service';
import { HcModal, HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent, SimpleModalOptions } from '@healthcatalyst/cashmere';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TermElement, TerminologyService } from '../../shared/services/terminology.service';
import { ChangeElement, ChangeService } from '../../shared/services/change.service';
import { OktaService } from '../../shared/services/okta.service';


/** List and Edit the Change Requests for Terminology */
@Component({
    selector: 'pd-admin-term-changes',
    templateUrl: './admin-term-changes.component.html',
    styleUrls: ['./admin-term-changes.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminTermChangesComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<TermChangeElement> = new HcTableDataSource();
    loading = true;
    loadError = false;
    displayedColumns: string[] = ['submitDate', 'publishDate', 'termId', 'name', 'usage', 'contributorName'];
    categories = ['Clinical', 'General', 'Health Catalyst', 'Industry', 'Technical'];
    types = ['Abbreviation', 'General usage'];
    pageSize: number = 10;
    addChangeControl = new FormGroup({
        name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        usage: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        comments: new FormControl('', {nonNullable: true}),
        contributorName: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
        contributorEmail: new FormControl<string>('', {nonNullable: true, validators: [Validators.required, Validators.email]})
    });
    adminTermControl = new FormGroup({
        id: new FormControl(0, {nonNullable: true}),
        dateAdded: new FormControl(new Date(), {nonNullable: true, validators: [Validators.required]}),
        name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        usage: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        types: new FormControl<string[]>([], {nonNullable: true, validators: [Validators.required]}),
        categories: new FormControl<string[]>([], {nonNullable: true, validators: [Validators.required]}),
        example: new FormControl('', {nonNullable: true})
    });
    ignoreDate = new Date(0);
    deleteDate = new Date(this.ignoreDate.getTime() + 1000);
    changeModalLoading = false;
    termExists = false;
    changeTarget: TermElement;
    private modalRef: HcModal<TemplateRef<any>>;

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    @ViewChild('reviewChangeModal')
    reviewChangeModal: TemplateRef<any>;

    get listLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }
    
    constructor ( 
        private terminologyService: TerminologyService,
        private modalService: ModalService,
        private termChangeService: TermChangeService,
        private changeService: ChangeService,
        private oktaService: OktaService
    ) {}

    ngOnInit(): void {
        this.termChangeService.termChangesUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.dataSource = new HcTableDataSource(list);
                if ( this.sort && this.paginator ) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                }
            } else {
                this.loadError = this.termChangeService.loadFailed;
                if ( !this.loadError ) {
                    this.loadTermChanges();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        setTimeout(() => {
            this.sort.sort( { disableClear: false, id: 'submitDate', start: 'desc' } );
        });
    }

    applyFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }


    loadTermChanges(): void {
        this.termChangeService.refresh();
    }

    addRequest(content: TemplateRef<any>): void {
        const options: ModalOptions = {
            size: 'md'
        };
        this.addChangeControl.reset();
        this.modalService.open(content, options);
    }

    submitRequest(): void {
        const target: TermChangeElement = {
            id: null,
            submitDate: new Date(),
            publishDate: null,
            termId: null,
            name: this.addChangeControl.controls['name'].value,
            usage: this.addChangeControl.controls['usage'].value,
            comment: this.addChangeControl.controls['comments'].value,
            contributorName: this.addChangeControl.controls['contributorName'].value,
            contributorEmail: this.addChangeControl.controls['contributorEmail'].value,
        };
        this.termChangeService.add( target );
    }

    requestClick( element: TermChangeElement): void {
        const options: ModalOptions = {
            size: 'xl',
            data: element
        };
        this.termExists = false;
        if ( element.termId ) {
            this.changeModalLoading = true;
            if ( !this.terminologyService.termList.value || this.terminologyService.termList.value.length == 0 ) {
                this.terminologyService.termsUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
                    if (list) {
                        this.patchChangeModal(element);
                    }
                });
                this.terminologyService.refresh();
            } else {
                this.patchChangeModal(element);
            }
        } else {
            this.adminTermControl.reset();
        }

        this.modalRef = this.modalService.open(this.reviewChangeModal, options);
    }

    patchChangeModal( element: TermChangeElement ) {
        this.adminTermControl.reset();
        if ( this.terminologyService.termList.value ) {
            for ( let i=0; i < this.terminologyService.termList.value.length; i++ ) {
                if ( this.terminologyService.termList.value[i].id == element.termId ) {
                    this.changeTarget = this.terminologyService.termList.value[i];
                    this.adminTermControl.patchValue({
                        id: this.terminologyService.termList.value[i].id,
                        name: this.terminologyService.termList.value[i].name,
                        usage: this.terminologyService.termList.value[i].usage,
                        types: this.terminologyService.termList.value[i].types.split(','),
                        categories: this.terminologyService.termList.value[i].categories.split(','),
                        example: this.terminologyService.termList.value[i].example
                    });
                    break;
                }
            }
        }
        this.changeModalLoading = false;
    }

    ignoreChange( element: TermChangeElement ) {
        element.publishDate = new Date(0);
        this.termChangeService.update ( element );
        this.modalRef.close();
    }

    publishChange( element: TermChangeElement ) {
        element.publishDate = new Date();
        this.termChangeService.update ( element );
        const today = new Date();

        if ( element.termId ) {
            const target: TermElement = {
                id: this.adminTermControl.controls['id'].value,
                name: this.adminTermControl.controls['name'].value,
                usage: this.adminTermControl.controls['usage'].value,
                types: this.adminTermControl.controls['types'].value.toString(),
                categories: this.adminTermControl.controls['categories'].value.toString(),
                example: this.adminTermControl.controls['example'].value,
                dateAdded: this.adminTermControl.controls['dateAdded'].value
            };
            let changeArray: ChangeElement[] = [];
            if ( target.name !== this.changeTarget.name ) {
                const change: ChangeElement = {
                    item: this.changeTarget.id,
                    name: target.name,
                    category: "terminology",
                    field: "name",
                    previous: this.changeTarget.name,
                    update: target.name,
                    user: this.oktaService.username.value,
                    date: today.toISOString()
                }
                changeArray.push( change );
            }
            if ( target.usage !== this.changeTarget.usage ) {
                const change: ChangeElement = {
                    item: this.changeTarget.id,
                    name: target.name,
                    category: "terminology",
                    field: "usage",
                    previous: this.changeTarget.usage,
                    update: target.usage,
                    user: this.oktaService.username.value,
                    date: today.toISOString()
                }
                changeArray.push( change );
            }
            if ( target.types !== this.changeTarget.types ) {
                const change: ChangeElement = {
                    item: this.changeTarget.id,
                    name: target.name,
                    category: "terminology",
                    field: "types",
                    previous: this.changeTarget.types,
                    update: target.types,
                    user: this.oktaService.username.value,
                    date: today.toISOString()
                }
                changeArray.push( change );
            }
            if ( target.categories !== this.changeTarget.categories ) {
                const change: ChangeElement = {
                    item: this.changeTarget.id,
                    name: target.name,
                    category: "terminology",
                    field: "categories",
                    previous: this.changeTarget.categories,
                    update: target.categories,
                    user: this.oktaService.username.value,
                    date: today.toISOString()
                }
                changeArray.push( change );
            }
            if ( target.example !== this.changeTarget.example ) {
                const change: ChangeElement = {
                    item: this.changeTarget.id,
                    name: target.name,
                    category: "terminology",
                    field: "example",
                    previous: this.changeTarget.example,
                    update: target.example,
                    user: this.oktaService.username.value,
                    date: today.toISOString()
                }
                changeArray.push( change );
            }
            this.terminologyService.update( target );
            this.changeService.add( changeArray );
        } else {
            const termCount = this.terminologyService.termList.value ? this.terminologyService.termList.value.length : 0;
            for ( let i=0; i < termCount; i++ ) {
                if ( this.terminologyService.termList.value && this.adminTermControl.controls['name'].value === this.terminologyService.termList.value[i].name ) {
                    this.adminTermControl.controls['name'].setErrors({'unique': false});
                    this.termExists = true;
                    break;
                }
            }

            if ( !this.termExists ) {
                const target: TermElement = {
                    id: -1,
                    name: this.adminTermControl.controls['name'].value,
                    usage: this.adminTermControl.controls['usage'].value,
                    types: this.adminTermControl.controls['types'].value.toString(),
                    categories: this.adminTermControl.controls['categories'].value.toString(),
                    example: this.adminTermControl.controls['example'].value,
                    dateAdded: this.adminTermControl.controls['dateAdded'].value
                };
                const change: ChangeElement = {
                    item: -1,
                    name: target.name,
                    category: "terminology",
                    field: target.name,
                    previous: "--",
                    update: "Term Added",
                    user: this.oktaService.username.value,
                    date: today.toISOString()
                }
                this.terminologyService.add( target, change );
                this.modalRef.close();
            }
        }
    }

    deleteTerm( element: TermChangeElement ): void {
        this.modalRef.close();

        const target: TermElement = {
            id: this.adminTermControl.controls['id'].value,
            name: this.adminTermControl.controls['name'].value,
            usage: this.adminTermControl.controls['usage'].value,
            types: this.adminTermControl.controls['types'].value.toString(),
            categories: this.adminTermControl.controls['categories'].value.toString(),
            example: this.adminTermControl.controls['example'].value,
            dateAdded: this.adminTermControl.controls['dateAdded'].value
        };

        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the term: <strong>' + element.name + '</strong>?<p>',
            headerText: 'Delete Term',
            confirmButtonText: 'Delete Term',
            icon: ''
        };
        this.modalService.confirmDestructive(options)
            .pipe(take(1),filter(confirmed => confirmed))
            .subscribe(() => {
                element.publishDate = new Date(this.ignoreDate.getTime() + 1000);
                this.termChangeService.update ( element );
                this.terminologyService.remove( target );
                const today = new Date();
                const change: ChangeElement = {
                    item: target.id,
                    name: target.name,
                    category: "terminology",
                    field: target.name,
                    previous: "--",
                    update: "Term Removed",
                    user: this.oktaService.username.value,
                    date: today.toISOString()
                };
                this.changeService.add( [change] );
            });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}